import { ApiMethodType } from '@celito.clients/enums';
import { performRequest } from '@celito.clients/network';
import { FilterDto, ObjectAttributeType } from '@celito.clients/types';
import {
  BaseEntity,
  BaseEntityWithName,
} from 'apps/web-client/src/types/base-entity';
import Endpoints from 'libs/core/src/config/endpoint';

import { CriteriaConditionPayloadType } from '../../../../../../../libs/shared/src/lib/criteria-condition-type/criteria-condition-type.model';
import {
  IActionAtomicSecurity,
  IAttributeAtomicSecurity,
} from '../admin-ui-object/tabs/admin-ui-lifecycle/admin-ui-lifecycle-details/components/admin-ui-lifecycle-details-stages/admin-ui-lifecycle-details-stages.model';

export interface IStages {
  label: string;
  name: string;
  ownerType: string;
  group: LifeCycleStageGroup;
  isActive: boolean;
  description: string;
}

export enum LifeCycleStageEntryValidationTypeEnum {
  FIELD = 'FIELD',
  STATE_OF_RECORD = 'STATE OF RECORD',
}

export declare interface ValidationSchema {
  type: LifeCycleStageEntryValidationTypeEnum;
  config: FieldConfig;
}
export interface FieldConfig {
  field: string;
  value: string;
}

export declare interface LifeCycleStageEntryAction {
  performOnConditions?: FilterDto[];
  actions: RecordStageEntryActionSchema[];
}

export declare interface RecordStageEntryActionSchema {
  name: string;
}

export declare interface LifeCycleStageUserAction {
  performOnConditions?: FilterDto[];
  actions: RecordUserActionSchema[];
}
export declare interface RecordUserActionSchema {
  name: string;
}

export declare interface RoleAttributeAtomicSecurity {
  roleName: string;
  roleLabel?: string;
  atomicSecurity: AttributeAtomicSecurityRuleSchema[];
}

export enum LifeCycleStageAttributeAtomicSecurityPermissionEnum {
  EDIT = 'Edit',
  VIEW = 'View',
  HIDE = 'Hide',
}

export declare interface AttributeAtomicSecurityRuleSchema {
  fieldName: string;
  fieldKey: string;
  fieldColumnName: string;
  fieldLabel: string;
  permission: LifeCycleStageAttributeAtomicSecurityPermissionEnum;
}

export declare interface LifeCycleStageAttributeAtomicSecurity {
  defaultAtomicSecurity: AttributeAtomicSecurityRuleSchema[];
  roleAtomicSecurity?: RoleAttributeAtomicSecurity[];
}

export enum LifeCycleStageUserActionsAtomicSecurityPermissionEnum {
  EXECUTE = 'Execute',
  VIEW = 'View',
  HIDE = 'Hide',
}

export declare interface UserActionAtomicSecurityRuleSchema {
  actionName: string;
  permission: LifeCycleStageUserActionsAtomicSecurityPermissionEnum;
}

export declare interface RoleUserActionsAtomicSecurity {
  roleName: string;
  roleLabel: string;
  atomicSecurity: UserActionAtomicSecurityRuleSchema[];
}

export declare interface LifeCycleStageUserActionsAtomicSecurity {
  defaultAtomicSecurity: UserActionAtomicSecurityRuleSchema[];
  roleAtomicSecurity?: RoleUserActionsAtomicSecurity[];
}

export declare interface LifeCycleStageGroup {
  name: string;
  displayActively: boolean;
  displayOrder: number;
  label: string;
}

export enum MetadataUpdateRecordActionEnum {
  UPDATE = 'update',
  EDIT = 'edit',
  RETIRE = 'retire',
}

export declare interface LifecycleActionTypeRules {
  actionType: MetadataUpdateRecordActionEnum;
  showSaveButton: boolean;
  showSubmitButton: boolean;
}

export declare interface ILifeCycleStage extends BaseEntityWithName {
  lifecycle: ILifeCycle;
  description?: string;
  entryCriteria?: CriteriaConditionPayloadType[];
  entryActions?: LifeCycleStageEntryAction[];
  userActions?: LifeCycleStageUserAction[];
  taskActions?: LifeCycleStageUserAction[];
  workflowActions?: LifeCycleStageUserAction[];
  attributeAtomicSecurity: LifeCycleStageAttributeAtomicSecurity;
  userActionsAtomicSecurity: LifeCycleStageUserActionsAtomicSecurity;
  workflowActionsAtomicSecurity: LifeCycleStageUserActionsAtomicSecurity;
  taskActionsAtomicSecurity: LifeCycleStageUserActionsAtomicSecurity;
  group?: LifeCycleStageGroup;
  lifecycleStageGroup?: LifeCycleStageGroup;
  actionTypeRules?: LifecycleActionTypeRules;
}
export declare interface RecordEventActionSchema {
  name: string;
}

export declare interface EventAction {
  performOnConditions?: FilterDto[];
  actions: RecordEventActionSchema[];
}

export declare interface LifeCycleEventActionsConfig {
  name: string;
  description?: string;
  eventActions: EventAction[];
}
export declare interface CrudPermissionForState {
  stageLabel: string | null;
  stageName: string | null;
  allowCreate: boolean;
  allowRead: boolean;
  allowUpdate: boolean;
  allowDelete: boolean;
}
export declare interface LifeCycleRolePermission {
  roleName: string;
  roleLabel: string;
  statePermissions: CrudPermissionForState[];
}

export interface ILifeCycle extends BaseEntity {
  object: ObjectAttributeType;
  description?: string;
  eventActions?: LifeCycleEventActionsConfig;
  rolePermissions: LifeCycleRolePermission[];
  stages: ILifeCycleStage[];
}

export interface IObjectLifeCycle {
  label: string;
  name: string;
  isActive: boolean;
  createdAtUtc: Date;
  deleteAtUtc: Date;
  modifiedAtUtc: Date;
  description: string;
  onwerType: string;
  rolePermissions: LifeCycleRolePermission[];
  stages: ILifeCycleStage[];
  eventActions: LifeCycleEventActionsConfig;
}

interface IObjectLifeCycleCreation {
  label: string;
  description: string;
}

export declare interface UpdateLifeCycleRequestPayload {
  label?: string;
  description?: string;
  eventActions?: LifeCycleEventActionsConfig;
  rolePermissions?: LifeCycleRolePermission[];
}

export enum RelatedObjectsLifecycleStateComparisionTypeEnum {
  ALL_RECORDS_STATE_EQUAL = 'AllRecordsStateEqual',
  NO_RECORDS_STATE_EQUAL = 'NoRecordsStateEqual',
  AT_LEAST_ONE_RECORD_STATE_EQUAL = 'AtLeastOneRecordStateEqual',
}

export interface UpdateLifeCycleStagePayload {
  attributeAtomicSecurity: IAttributeAtomicSecurity;
  userActionsAtomicSecurity: IActionAtomicSecurity;
  workflowActionsAtomicSecurity: IActionAtomicSecurity;
  taskActionsAtomicSecurity: IActionAtomicSecurity;
  entryActions: any;
  name: string;
  entryCriteria: CriteriaConditionPayloadType[] | null;
  userActions: any;
  taskActions: any;
  workflowActions: any;
  label: string;
  isActive: boolean;
  group: string;
  description?: string;
  ownerType: string;
}

export const getObjectLifeCycle = async (objectName: string) => {
  const respone = await performRequest<IObjectLifeCycle>(
    `${Endpoints.LIFECYCLE}/${objectName}`,
    ApiMethodType.GET
  );
  return respone.data;
};

export const postObjectLifecycle = async (
  objectName: string,
  payload: IObjectLifeCycleCreation
) => {
  const respone = await performRequest<IObjectLifeCycleCreation>(
    `${Endpoints.LIFECYCLE}/${objectName}`,
    ApiMethodType.POST,
    payload
  );
  return respone.data;
};

export const putObjectLifecycle = async (
  objectName: string,
  payload: UpdateLifeCycleRequestPayload
) => {
  const respone = await performRequest<ILifeCycle>(
    `${Endpoints.LIFECYCLE}/${objectName}`,
    ApiMethodType.PUT,
    payload
  );
  return respone.data;
};

export const putLyfecycleStage = async (
  stageName: string,
  payload: UpdateLifeCycleStagePayload
) => {
  const respone = await performRequest<ILifeCycle>(
    `${Endpoints.LIFECYCLE_STAGE}/${stageName}`,
    ApiMethodType.PUT,
    payload
  );
  return respone.data;
};

export const postLifecycleStage = async (
  payload: UpdateLifeCycleStagePayload,
  lifecycleObjectName: string
) => {
  const respone = await performRequest<ILifeCycle>(
    `${Endpoints.LIFECYCLE_STAGE}/${lifecycleObjectName}`,
    ApiMethodType.POST,
    payload
  );
  return respone.data;
};
